import type { FC } from 'react';
import React from 'react';
import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';

import { useRouteDataRef, useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import { WIKI, HOME } from '@confluence/named-routes/entry-points/routes.ts';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import {
	GO_HOME_SHORTCUT,
	UNIVERSAL_GO_HOME_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { HomeOnboardingTakeoverV2HomeNudge } from '@confluence/experiment-home-onboarding-takeover-v2/entry-points/HomeOnboardingTakeoverV2HomeNudge.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { EditorForYouSpotlight } from '@confluence/experiment-nth-user-orientation/entry-points/EditorForYouSpotlight.ts';
import { fg } from '@confluence/feature-gating';

import { KeyboardShortcutWrapper } from '../KeyboardShortcutWrapper.tsx';
import { i18n } from '../globalNavigationTranslations.ts';
import { ForYouIconComponent } from '../GlobalNavigationIcons.tsx';
import { useForYouConfig } from '../../PlatformNavigation/hooks/useForYouConfig.tsx';

import type { GlobalItemProps } from './globalItemProps.ts';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ '../analyticsCallbacks.ts'))
			.fireHomeClickedAnalytics,
);

const isOnHomeSelector = {
	selector: (routeUrl: string | undefined) => {
		if (!routeUrl) {
			return false;
		}
		return routeUrl === HOME.toUrl() || routeUrl === WIKI.toUrl();
	},
};

export const ForYouItemGlobal: FC<GlobalItemProps> = () => {
	//takes globalItemProps but is not customizable so doesn't use them
	const routeDataRef = useRouteDataRef();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);

	const isSelected = useRouteUrl(isOnHomeSelector);

	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const homeUrl = HOME.toUrl();

	return (
		<KeyboardShortcutWrapper
			href={homeUrl}
			deprecatedKeyboardShortcut={!(isLiveEditMode || isOnEditRoute) ? GO_HOME_SHORTCUT : undefined}
			keyboardShortcut={UNIVERSAL_GO_HOME_SHORTCUT}
			isAppNavigationShortcut
			onShortcutTriggered={fireClickAnalytics}
		>
			<HomeOnboardingTakeoverV2HomeNudge>
				<EditorForYouSpotlight>
					<LinkMenuItem
						href={homeUrl}
						elemBefore={ForYouIconComponent}
						isSelected={isSelected}
						onClick={fireClickAnalytics}
					>
						<FormattedMessage {...i18n.forYou} />
					</LinkMenuItem>
				</EditorForYouSpotlight>
			</HomeOnboardingTakeoverV2HomeNudge>
		</KeyboardShortcutWrapper>
	);
};

export const ForYouItemPlatform: FC<GlobalItemProps> = () => {
	const {
		forYouItem: { url, isSelected, onClick, ItemWrapper },
	} = useForYouConfig();

	return (
		<ItemWrapper>
			<LinkMenuItem
				href={url}
				elemBefore={ForYouIconComponent}
				isSelected={isSelected}
				onClick={onClick}
			>
				<FormattedMessage {...i18n.forYou} />
			</LinkMenuItem>
		</ItemWrapper>
	);
};

export const ForYouItem: FC<GlobalItemProps> = (props) => {
	return fg('navx-global-side-nav-confluence') ? (
		<ForYouItemPlatform {...props} />
	) : (
		<ForYouItemGlobal {...props} />
	);
};
